/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
    createReactAdapter,
    type ReactDesignComponentType,
} from '@salesforce/storefront-next-runtime/design/react/core';
import { ComponentRegistry } from '@salesforce/storefront-next-runtime/design';

type RegistryImporter<TProps> = Parameters<
    ComponentRegistry<TProps, ReactDesignComponentType<TProps>>['registerImporter']
>[1];
type RegistryLoaderNames<TProps> = Parameters<
    ComponentRegistry<TProps, ReactDesignComponentType<TProps>>['registerImporter']
>[2];
type ReactRegistryAdapter<TProps> = ReturnType<typeof createReactAdapter<TProps>>;

// SDK 1.2.0 creates a new decorated component on every registry lookup. Cache at the
// adapter boundary so both lazy and concretely registered components retain their type.
function stabilizeReactAdapter<TProps>(adapter: ReactRegistryAdapter<TProps>): ReactRegistryAdapter<TProps> {
    const decoratedComponents = new WeakMap<object, ReactDesignComponentType<TProps>>();
    const decorateComponent = adapter.decorateComponent.bind(adapter);
    adapter.decorateComponent = (component) => {
        const cached = decoratedComponents.get(component);
        if (cached) return cached;

        const decorated = decorateComponent(component);
        decoratedComponents.set(component, decorated);
        return decorated;
    };
    return adapter;
}

// RGBARRY-CUSTOMIZATION: backport concrete component loading and registration while the app remains on SDK 1.2.0
class BackportComponentRegistry<TProps> extends ComponentRegistry<TProps, ReactDesignComponentType<TProps>> {
    private readonly reactAdapter: ReactRegistryAdapter<TProps>;
    private readonly importers = new Map<string, RegistryImporter<TProps>>();
    private readonly registeredFallbacks = new Map<string, ReactDesignComponentType<TProps>>();
    private readonly registeredComponents = new Map<string, ReactDesignComponentType<TProps>>();
    private readonly inFlightRegistrations = new Map<string, Promise<void>>();
    private readonly registrationErrors = new Map<string, Error>();
    private readonly registeredComponentIds = new Set<string>();

    constructor(adapter: ReactRegistryAdapter<TProps>) {
        super({ adapter });
        this.reactAdapter = adapter;
    }

    override registerImporter(
        id: string,
        importer: RegistryImporter<TProps>,
        loaderNames?: RegistryLoaderNames<TProps>
    ): void {
        this.importers.set(id, importer);
        this.registeredFallbacks.delete(id);
        this.registeredComponents.delete(id);
        this.registeredComponentIds.delete(id);
        this.inFlightRegistrations.delete(id);
        this.registrationErrors.delete(id);
        super.registerImporter(id, importer, loaderNames);
    }

    loadAndRegister(id: string): Promise<void> {
        const importer = this.importers.get(id);
        if (!importer) {
            const error = new Error(`Unknown Page Designer component type "${id}"`);
            this.registrationErrors.set(id, error);
            return Promise.reject(error);
        }
        if (this.registeredComponentIds.has(id)) return Promise.resolve();

        const pending = this.inFlightRegistrations.get(id);
        if (pending) return pending;
        this.registrationErrors.delete(id);

        let importedModule: ReturnType<RegistryImporter<TProps>>;
        try {
            importedModule = importer();
        } catch (cause) {
            const error =
                cause instanceof Error ? cause : new Error(`Failed to load Page Designer component "${id}"`, { cause });
            this.registrationErrors.set(id, error);
            return Promise.reject(error);
        }

        const registration = Promise.resolve(importedModule)
            .then((module) => {
                if (!module.default) throw new Error(`Page Designer component "${id}" has no default export`);
                const currentImporter = this.importers.get(id);
                if (!currentImporter) throw new Error(`Page Designer component registration for "${id}" was cancelled`);
                if (currentImporter !== importer) return this.loadAndRegister(id);
                if (module.fallback) this.registeredFallbacks.set(id, module.fallback);
                this.registeredComponents.set(id, module.default);
                this.registerComponent(id, module.default);
                this.registeredComponentIds.add(id);
            })
            .catch((cause: unknown) => {
                const error =
                    cause instanceof Error
                        ? cause
                        : new Error(`Failed to load Page Designer component "${id}"`, { cause });
                if (this.importers.get(id) === importer) this.registrationErrors.set(id, error);
                throw error;
            });
        const trackedRegistration = registration.finally(() => {
            if (this.inFlightRegistrations.get(id) === trackedRegistration) {
                this.inFlightRegistrations.delete(id);
            }
        });

        this.inFlightRegistrations.set(id, trackedRegistration);
        return trackedRegistration;
    }

    /** Whether a component's concrete module export has already been registered. */
    hasConcreteComponent(id: string): boolean {
        return this.registeredComponentIds.has(id);
    }

    getRegistrationError(id: string): Error | undefined {
        return this.registrationErrors.get(id);
    }

    clearRegistrationError(id: string): void {
        this.registrationErrors.delete(id);
    }

    /** Return and remove a failed registration for a terminal SSR render attempt. */
    consumeRegistrationError(id: string): Error | undefined {
        const error = this.getRegistrationError(id);
        this.clearRegistrationError(id);
        return error;
    }

    override getComponent(id: string): ReactDesignComponentType<TProps> | null {
        const registered = this.registeredComponents.get(id);
        return registered ? this.reactAdapter.decorateComponent(registered) : super.getComponent(id);
    }

    override getFallback(id: string): ReactDesignComponentType<TProps> | undefined {
        return this.registeredFallbacks.get(id) ?? super.getFallback(id);
    }

    override clear(): void {
        this.importers.clear();
        this.registeredFallbacks.clear();
        this.registeredComponents.clear();
        this.inFlightRegistrations.clear();
        this.registrationErrors.clear();
        this.registeredComponentIds.clear();
        super.clear();
    }
}

/**
 * Factory function to create a React-specific component registry
 * with the React adapter pre-configured.
 */
export function createReactComponentRegistry<TProps>() {
    return new BackportComponentRegistry<TProps>(stabilizeReactAdapter(createReactAdapter<TProps>()));
}

/**
 * Global component registry instance.
 * Used throughout the application to discover and load components.
 *
 * This singleton instance is configured with:
 * - React adapter for React-specific behavior
 * - Design mode decorator for Page Designer integration
 * - Static component registration via Vite plugin (no dynamic discovery needed)
 * - Component metadata handled via API (not stored in registry)
 *
 * Heterogeneous component prop shapes are registered here; `any` is required so the
 * registry covariantly accepts any prop type. Switching to `unknown` would force every
 * call site to narrow before forwarding props, which defeats the runtime-driven design.
 */
/* oxlint-disable-next-line @typescript-eslint/no-explicit-any */
export const registry = createReactComponentRegistry<any>();
